'use client'

import { GetProductDetailsQuery } from '@/app/[lang]/(full)/products/[itemId]/getProductDetails.generated'
import { ItemStatusLabel } from '@/app/api/types/redpim.enum'
import { Item, Product, Specification } from '@/app/api/types/redpim.types'
import RedcardsBlock from '@/components/blocks/RedcardsBlock/RedcardsBlock'
import { RedcardsBlockFragment } from '@/components/blocks/RedcardsBlock/RedcardsBlock.generated'
import { LocalizedLink } from '@/components/LocalizedLink'
import ProductDetailsTechnical from '@/components/Products/ProductDetails/ProductDetailsTechnical/ProductDetailsTechnical'
import { useUnitContext } from '@/hooks/useUnitContext'
import { redpimToCorpoLocale } from '@/lib/locale.utils'
import {
	getSpecs,
	getSpecsByUnit,
} from '@/utils/getSpecsFromItemSpecificationBlocs'
import * as countries from 'i18n-iso-countries'
import { getSupportedLanguages } from 'i18n-iso-countries'
import { getName } from 'i18n-iso-countries'
import Image from 'next/image'
import { useEffect, useMemo, useState } from 'react'

const supportedLanguages = getSupportedLanguages()

supportedLanguages.forEach((lang) => {
	countries.registerLocale(require(`i18n-iso-countries/langs/${lang}.json`))
})

type ProductDetailsDescriptionProps = {
	lang: string
	item: Item
	product: Product
	items?: Item[]
	fragment: GetProductDetailsQuery
}
export default function ProductDetailsDescription({
	lang,
	item,
	product,
	items,
	fragment,
}: ProductDetailsDescriptionProps) {
	const [specifications, setSpecifications] = useState<Specification[]>([])

	const { unit } = useUnitContext()
	const redpimLocale = redpimToCorpoLocale(lang as string)

	const formattedLang = lang.split('-')[0]

	const restrictedCountriesNames = useMemo(
		() =>
			(item?.countriesRestrictedIn ?? '')
				.split(',')
				.map((country) => country.trim())
				.map((country) => getName(country, formattedLang))
				.filter((country) => country != null),
		[item?.countriesRestrictedIn, formattedLang],
	)

	useEffect(() => {
		const newSpecs: Specification[] = []

		if (item.specifications)
			item.specifications.map((spec: Specification) => {
				if (spec.nameIntl && spec.value) {
					if (
						(spec.unitCategory && spec.unitCategory === unit) ||
						spec.unitCategory === 'Universal'
					)
						newSpecs.push(spec)
				}
			})
		setSpecifications(newSpecs)
	}, [unit])

	return (
		<>
			<div className="container-auto relative lg:flex justify-between z-0">
				<Image
					src="/images/dots.svg"
					alt="dots"
					width="280"
					height="280"
					className="hidden lg:block absolute top-[-17rem] right-0 z-0 w-auto h-auto"
				/>

				<div className="lg:w-1/2 pt-8 lg:pt-20">
					{item.statusLabel !== ItemStatusLabel.ACTIVE && (
						<div className="mb-8 bg-gray-50 py-3 rounded-md flex flex-col items-center justify-center">
							<div className="text-base lg:text-lg font-semibold">
								🚫 {fragment.translations.notAvailable}
							</div>
							{item.replacementMaterial && (
								<div className="text-base lg:text-lg font-semibold">
									<LocalizedLink
										locale={lang}
										href={`/products/${item.replacementMaterial}`}
										className="text-red-500 underline"
										rel={'nofollow'}
									>
										{fragment.translations.replacementMaterial}
									</LocalizedLink>
								</div>
							)}
						</div>
					)}
					<div className="mb-8 bg-white">
						<div className="h4">
							{fragment.translations.productDescriptionTitle}
						</div>
						<p>
							{item?.marketingDescriptionsIntl?.[redpimLocale] ||
								product.descriptionIntl?.[redpimLocale] ||
								''}
						</p>
						{item?.category && (
							<p className="pt-8">
								{fragment.translations.partOf}{' '}
								<LocalizedLink
									locale={lang}
									href={`/products/series/${item.category.pimId}`}
									className="text-red-500 underline"
									rel={'nofollow'}
								>
									{item.category.nameIntl[redpimLocale]}
								</LocalizedLink>
							</p>
						)}
						{restrictedCountriesNames && restrictedCountriesNames.length > 0 ? (
							<p className="pt-4">
								<strong>
									{fragment.translations.listOfNonSellingCountries}{' '}
								</strong>
								{restrictedCountriesNames.join(', ')}
							</p>
						) : null}
					</div>

					<div className="mb-16">
						<p className="text-md font-bold">
							{fragment.translations.identification}
						</p>

						<div className="flex justify-between mt-4 px-6 py-5 bg-white border border-gray-100 rounded-2xl">
							<span className="font-bold">
								{fragment.translations.partNumber}
							</span>
							{item?.partnumber}
						</div>
					</div>
					<ProductDetailsTechnical
						lang={lang}
						fragment={fragment}
						specifications={specifications}
					/>

					<div className="flex justify-end mt-16 p-2 pr-4">
						<div className="flex justify-between max-w-44 w-full">
							{getSpecs(item.specificationBlocks, lang as string)
								.slice(0, 2)
								.map((val) => Object.keys(val))
								.map((val, i) => (
									<div key={i} className="text-sm font-bold">
										{val}
									</div>
								))}
						</div>
					</div>

					<div className="py-2 px-4 rounded-2xl border border-gray-100">
						{items?.map((el, i) => (
							<div
								className="border-b border-gray-100 last-of-type:border-b-0"
								key={`${el.partnumber}-${i}`}
							>
								<LocalizedLink
									locale={lang}
									prefetch={false}
									href={`/products/${el.partnumber}`}
									className="flex items-center w-full py-3 gap-3"
									rel="nofollow"
								>
									<input
										type="radio"
										defaultChecked={el.partnumber === item.partnumber}
									/>
									<div className="flex justify-between w-full">
										<div className="w-full text-md truncate">{el.model}</div>
										{el.specificationBlocks && (
											<div className="flex justify-between max-w-44 w-full">
												{getSpecsByUnit(el.specificationBlocks, unit)
													.slice(0, 2)
													.map((val) => Object.values(val))
													.map((val, i) => (
														<div key={i}>{val}</div>
													))}
											</div>
										)}
									</div>
								</LocalizedLink>
							</div>
						))}
					</div>
				</div>
			</div>

			{fragment.productDetail.blocks[0].__typename ===
				'RedcardsBlockRecord' && (
				<RedcardsBlock
					fragment={fragment.productDetail.blocks[0] as RedcardsBlockFragment}
				/>
			)}
		</>
	)
}
